import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  ContentPanel,
  DataTable,
  DataTableContainer,
  Divider,
  InfiniteScroll,
  PageFilters,
  SimpleLabel,
  Text,
} from '@hopskipdrive/react-components';
import { actions, Can, subjects } from '@rideiq/contexts/AbilityContext';
import { ActionBar, ButtonLink, SearchNavBar } from '@rideiq/shared/components';
import { PageErrorFallback } from '@rideiq/shared/components/PageErrorFallback';
import { QueryErrorState } from '@rideiq/shared/components/QueryErrorState';
import { colorPalette, variables } from '@rideiq/shared/components/variables';
import { ErrorBoundary } from '@rideiq/utils/errorReporting/ErrorBoundary';

import { analyticsPage } from '../../../api/services/Analytics';
import * as Constants from '../../../constants';
import { useQuery } from '../../../hooks/useQuery';
import { useTimezone } from '../../../hooks/useTimezone';
import { useNavigation } from '../../../shared/hooks/useNavigation';
import { formatDateString } from '../../../utils/helpers';
import { useCalendarsQuery } from '../../hooks/useCalendarsQuery';

import { CalendarsContainer } from './styles';

const Calendars: React.FC = () => {
  const { calendars, queryParams, handlePagination, handleQuery, fetchCalendars, isLoading, isError } =
    useCalendarsQuery();
  const { navigateTo } = useNavigation();
  const { formatInTimezone } = useTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const tableHeightOffset = variables.headingHeights.withTitleTabsFilters;

  useQuery({ query: queryParams.query }, (initialParams) => handleQuery(initialParams.get('query') ?? ''));

  useEffect(() => {
    const initialQuery = searchParams.get('query');

    if (initialQuery !== null && initialQuery.trim().length < Constants.MINIMUM_SEARCH_STRING_LENGTH) {
      const nextParams = new URLSearchParams(searchParams);

      nextParams.delete('query');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    analyticsPage('Calendars');
  }, []);

  const getLocationCount = ({ locationCount }) => {
    return locationCount === 0 ? (
      <SimpleLabel label="No Locations" textColor="text" />
    ) : (
      <SimpleLabel label={locationCount} textColor="text" />
    );
  };
  const getLastUpdated = ({ updatedAt, lastEditor }: Calendar) =>
    `${formatInTimezone(updatedAt, 'MMM dd, yyyy')} ${lastEditor ? `by ${lastEditor.fullName}` : ''}`;

  return (
    <ErrorBoundary FallbackComponent={PageErrorFallback}>
      <CalendarsContainer>
        <ActionBar>
          <ActionBar.Title title="Calendars" />
          <ActionBar.SideControls>
            <Can I={actions.add} a={subjects.calendar}>
              <ButtonLink color="primary" size="large" to="/calendars/new">
                Create Calendar
              </ButtonLink>
            </Can>
          </ActionBar.SideControls>
        </ActionBar>
        <Divider bgColor={colorPalette.neutralVariant90} opacity={0.5} />
        <PageFilters>
          <SearchNavBar
            debounceHandleQuery={handleQuery}
            placeholder="Search calendars by name"
            query={queryParams.query}
          />
        </PageFilters>
        <ContentPanel mt="24px">
          {isError ? (
            <QueryErrorState
              message="Something went wrong loading calendars. Please try again."
              tableHeightOffset={tableHeightOffset}
              onRetry={fetchCalendars}
            />
          ) : (
            <DataTableContainer heightOffset={tableHeightOffset} minWidth="1052px">
              <InfiniteScroll
                key={queryParams.query}
                itemCount={calendars.length}
                pageNumber={queryParams.page}
                perPage={queryParams.perPage}
                handleNextPage={handlePagination}
              >
                <DataTable
                  stickyHeader={true}
                  rows={calendars}
                  isLoading={isLoading}
                  handleRowClick={({ slug }) => navigateTo(`/calendars/${slug}`)}
                  href={({ slug }) => `/calendars/${slug}`}
                  columns={[
                    {
                      key: 'name',
                      label: 'Name',
                      align: 'left',
                      maxWidth: '200px',
                    },
                    {
                      label: '# Locations',
                      align: 'left',
                      valueGetter: (rowData) => getLocationCount(rowData),
                    },
                    {
                      label: 'Last Updated',
                      align: 'left',
                      valueGetter: (rowData) => getLastUpdated(rowData),
                      minWidth: '221px',
                    },
                    {
                      key: 'extendsUntil',
                      label: 'Extends Until',
                      align: 'left',
                      valueSetter: (cellValue) => (
                        <span aria-label={cellValue ? undefined : 'Extends Until Empty'}>
                          {cellValue && formatDateString(cellValue, 'MMM dd, yyyy')}
                        </span>
                      ),
                    },
                  ]}
                  noRecordsMessage={getNoCalendarsMessage(tableHeightOffset, !!queryParams.query)}
                />
              </InfiniteScroll>
            </DataTableContainer>
          )}
        </ContentPanel>
      </CalendarsContainer>
    </ErrorBoundary>
  );
};

export default Calendars;

const getNoCalendarsMessage = (tableHeightOffset: number, isSearching: boolean) => {
  const minHeight = window.innerHeight - tableHeightOffset - variables.tableHeadHeight;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="1rem"
      justifyContent="center"
      alignItems="center"
      minHeight={minHeight}
      role="status"
    >
      <Text textColor="secondaryText" textAlign="center">
        {isSearching ? 'No calendars match your search.' : 'Sorry, no calendars have been added to this account yet.'}
      </Text>
      {!isSearching && (
        <ButtonLink color="primary" size="large" to="/calendars/new">
          Create Calendar
        </ButtonLink>
      )}
    </Box>
  );
};
